/* Shared "Drop a Hint" dialog — used by the wishlist page and the PDP buttons.
 * Exposes window.JNHint.open(items, opts), where items = [{ gid, handle, title }].
 * Collects a size per item + recipient email and/or mobile + optional note, then
 * POSTs { recipient, phone, message, items } to the App Proxy /apps/wishlist/hint.
 * Sending a hint requires the shopper to be logged in (the Worker attributes the
 * sender and rate-limits by it). */
(function () {
  'use strict';

  var CFG = window.JN_WISHLIST || {};
  var PROXY = CFG.proxyBase || '/apps/wishlist';
  var productCache = {};

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function isEmail(s) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim()); }
  function showErr(el, msg) { if (el) { el.textContent = msg; el.hidden = false; } }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { 'Accept': 'application/json' }, credentials: 'same-origin'
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { productCache[handle] = p; return p; })
      .catch(function () { return null; });
  }
  function sizeOptionIndex(product) {
    var opts = (product && product.options) || [];
    var names = opts.map(function (o) { return (typeof o === 'string' ? o : (o && o.name) || '').toLowerCase(); });
    var idx = names.indexOf('size');
    if (idx === -1) {
      for (var i = 0; i < names.length; i++) {
        if (names[i] && names[i] !== 'color' && names[i] !== 'colour' && names[i] !== 'title') { idx = i; break; }
      }
    }
    return idx === -1 && names.length ? 0 : idx;
  }
  function norm(s) { return String(s == null ? '' : s).trim().toUpperCase().replace(/\s*\/\s*/g, '/').replace(/\s+/g, ' '); }

  // Cup rank shared by matching and sorting. US (DD/DDD) and EU-style (E/F)
  // notations map to the same rank so a saved "34DD" matches a product's "34E".
  var CUP_RANK = { AA: 0, A: 1, B: 2, C: 3, D: 4, DD: 5, E: 5, DDD: 6, F: 6, FF: 6.5, G: 7, GG: 7.5, H: 8, HH: 8.5, I: 9, J: 10, K: 11 };
  function parseBra(value) {
    var m = norm(value).replace(/\s+/g, '').match(/^(\d{2,3})([A-Z]{1,4})$/);
    if (!m || !(m[2] in CUP_RANK)) return null;
    return { band: parseInt(m[1], 10), rank: CUP_RANK[m[2]] };
  }

  // Alpha apparel sizing (abbreviations + full words) for ordering.
  var WORD_TO_ABBR = {
    'EXTRA EXTRA SMALL': 'XXS', 'XX SMALL': 'XXS', 'XXS': 'XXS',
    'EXTRA SMALL': 'XS', 'X SMALL': 'XS', 'XS': 'XS',
    'SMALL': 'S', 'S': 'S',
    'MEDIUM': 'M', 'MED': 'M', 'M': 'M',
    'LARGE': 'L', 'L': 'L',
    'EXTRA LARGE': 'XL', 'X LARGE': 'XL', 'XL': 'XL',
    'EXTRA EXTRA LARGE': 'XXL', 'XX LARGE': 'XXL', 'XXL': 'XXL',
    'XXX LARGE': 'XXXL', 'XXXL': 'XXXL'
  };
  var ALPHA_RANK = { XXXS: -1, XXS: 0, XS: 1, S: 2, M: 3, L: 4, XL: 5, XXL: 6, XXXL: 7 };

  // Sort key: group bras (band, cup), then alpha sizes, then numeric, then text.
  function sizeSortKey(value) {
    var n = norm(value);
    var bra = parseBra(value);
    if (bra) return [0, bra.band, bra.rank, n];
    var abbr = WORD_TO_ABBR[n];
    if (abbr && abbr in ALPHA_RANK) return [1, ALPHA_RANK[abbr], 0, n];
    if (/^\d+(\.\d+)?$/.test(n)) return [2, parseFloat(n), 0, n];
    return [3, 0, 0, n];
  }
  function sortSizes(arr) {
    return arr.slice().sort(function (a, b) {
      var ka = sizeSortKey(a.value), kb = sizeSortKey(b.value);
      for (var i = 0; i < ka.length; i++) {
        if (ka[i] < kb[i]) return -1;
        if (ka[i] > kb[i]) return 1;
      }
      return 0;
    });
  }

  function sizesFor(product) {
    var idx = sizeOptionIndex(product);
    if (idx < 0 || !product || !product.variants) return [];
    var key = 'option' + (idx + 1), seen = {}, out = [];
    product.variants.forEach(function (v) {
      var val = v[key];
      if (val == null || val === '') return;
      if (String(val).toLowerCase() === 'default title') return;
      if (!(val in seen)) { seen[val] = out.length; out.push({ value: val, available: !!v.available }); }
      else if (v.available) { out[seen[val]].available = true; }
    });
    return sortSizes(out);
  }

  // ---- Remembered sizes (from the size-finder cookies) -------------------
  // jfq_bra_sz  = {"band","cup","full"} e.g. full "34C" (US cups: DD/DDD)
  // jfq_dress_sz = {"dressSize","pantySize"}; both cookie values URL-encoded.
  function readCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/+^])/g, '\\$1') + '=([^;]*)'));
      if (!m) return null;
      return JSON.parse(decodeURIComponent(m[1]));
    } catch (e) { return null; }
  }

  // Match a remembered size against this product's actual size chips.
  function preferredSizeFromCookie(sizes) {
    if (!sizes || !sizes.length) return '';
    var values = sizes.map(function (s) { return s.value; });
    var normed = values.map(norm);

    var strCands = [], braCand = null;
    var bra = readCookie('jfq_bra_sz');
    if (bra) {
      if (bra.full) strCands.push(bra.full);
      if (bra.band && bra.cup) strCands.push(String(bra.band) + String(bra.cup));
      braCand = parseBra(bra.full || (String(bra.band || '') + String(bra.cup || '')));
    }
    var dress = readCookie('jfq_dress_sz');
    if (dress) {
      if (dress.dressSize) strCands.push(dress.dressSize);
      if (dress.pantySize) strCands.push(dress.pantySize);
    }

    // 1) exact normalized match (alpha / dress / panty / same-notation bra).
    for (var i = 0; i < strCands.length; i++) {
      var c = norm(strCands[i]);
      if (!c) continue;
      var hit = normed.indexOf(c);
      if (hit !== -1) return values[hit];
    }
    // 2) structural bra match — same band + cup rank across US/EU notations.
    if (braCand) {
      for (var j = 0; j < values.length; j++) {
        var pb = parseBra(values[j]);
        if (pb && pb.band === braCand.band && pb.rank === braCand.rank) return values[j];
      }
    }
    return '';
  }

  function toast(msg) {
    var t = document.createElement('div');
    t.className = 'jn-wl-toast';
    t.textContent = msg;
    document.body.appendChild(t);
    requestAnimationFrame(function () { t.classList.add('is-in'); });
    setTimeout(function () { t.classList.remove('is-in'); setTimeout(function () { t.remove(); }, 300); }, 2600);
  }

  function sendHint(payload) {
    return fetch(PROXY + '/hint', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (r.ok) return r.json();
      return r.json().catch(function () { return {}; }).then(function (j) { return Promise.reject(j); });
    });
  }

  function redirectToLogin() {
    var base = CFG.loginUrl || '/customer_authentication/login';
    window.location.href = base + '?return_to=' + encodeURIComponent(window.location.pathname + window.location.search);
  }

  function open(items, opts) {
    opts = opts || {};
    if (!CFG.loggedIn) { redirectToLogin(); return; }
    Promise.all(items.map(function (it) {
      return fetchProduct(it.handle).then(function (p) {
        var sizes = sizesFor(p);
        var preSize = '', fixed = false;
        // A specific variant was chosen (PDP selection or the saved wishlist variant):
        // the size is known, so lock it in and skip the size prompt.
        if (it.variantId && p && p.variants) {
          var v = p.variants.filter(function (x) { return String(x.id) === String(it.variantId); })[0];
          if (v) { var idx = sizeOptionIndex(p); if (idx >= 0) { preSize = v['option' + (idx + 1)] || ''; if (preSize) fixed = true; } }
        }
        // Otherwise fall back to the size-finder cookie — a guess, so still editable.
        if (!preSize) preSize = preferredSizeFromCookie(sizes);
        return { it: it, sizes: sizes, preSize: preSize, fixed: fixed };
      });
    })).then(function (resolved) { showDialog(resolved, opts); });
  }

  function showDialog(resolved, opts) {
    var isSet = resolved.length > 1;

    if (typeof HTMLDialogElement === 'undefined') {
      var fbEmail = (window.prompt('Their email:') || '').trim();
      if (!isEmail(fbEmail)) return;
      var fbPhone = (window.prompt('Their mobile (optional):') || '').trim();
      var fbItems = resolved.map(function (r) {
        var s = r.sizes.length
          ? (window.prompt('Size for ' + r.it.title + '? (' + r.sizes.map(function (x) { return x.value; }).join(', ') + ')') || '')
          : '';
        return { id: r.it.gid, size: s };
      });
      sendHint({ recipient: fbEmail, phone: fbPhone, message: '', items: fbItems })
        .then(function () { toast('Hint sent ✓'); if (opts.onSent) opts.onSent(); })
        .catch(function () { window.alert('Sorry, the hint could not be sent.'); });
      return;
    }

    var allFixed = true;
    var itemsHtml = resolved.map(function (r, idx) {
      var body, hasSize, fixedAttr = '';
      if (r.fixed && r.preSize) {
        // Size already chosen — show it, no picker.
        body = '<div class="jn-hint-fixedsize">Size ' + escapeHtml(r.preSize) + '</div>';
        hasSize = '1'; fixedAttr = ' data-fixed-size="' + escapeHtml(r.preSize) + '"';
      } else if (r.sizes.length) {
        allFixed = false;
        body = '<div class="jn-hint-sizes">' + r.sizes.map(function (s) {
          var checked = (r.preSize && String(s.value) === String(r.preSize)) ? ' checked' : '';
          return '<label class="jn-hint-size' + (s.available ? '' : ' is-soldout') + '">' +
            '<input type="radio" name="jn-hint-size-' + idx + '" value="' + escapeHtml(s.value) + '"' + checked + '>' +
            '<span>' + escapeHtml(s.value) + (s.available ? '' : ' (sold out)') + '</span></label>';
        }).join('') + '</div>';
        hasSize = '1';
      } else {
        body = '<div class="jn-hint-nosize">One size</div>';
        hasSize = '0';
      }
      return '<div class="jn-hint-item" data-gid="' + escapeHtml(r.it.gid) + '" data-hassize="' + hasSize + '"' + fixedAttr + '>' +
        '<div class="jn-hint-item__title">' + escapeHtml(r.it.title) + '</div>' + body + '</div>';
    }).join('');

    var dlg = document.createElement('dialog');
    dlg.className = 'jn-hint-dialog';
    dlg.innerHTML =
      '<form class="jn-hint-dialog__inner" novalidate>' +
        '<button type="button" class="jn-hint-close" aria-label="Close" data-jn-hint-close>&times;</button>' +
        '<h2 class="jn-hint-dialog__title">' + (isSet ? 'Drop a hint — the set' : 'Drop a hint') + '</h2>' +
        '<p class="jn-hint-dialog__sub">' + (allFixed
          ? 'Tell us who should get the hint.'
          : (isSet
            ? 'Pick your size for each piece, then tell us who should get the hint.'
            : 'Pick your size, then tell us who should get the hint.')) + '</p>' +
        '<div class="jn-hint-items">' + itemsHtml + '</div>' +
        '<p class="jn-hint-contact">We’ll email the hint. Add a mobile to text it too (optional).</p>' +
        '<label class="jn-hint-field"><span>Their email</span>' +
          '<input type="email" name="jn-hint-email" required placeholder="friend@example.com" autocomplete="off"></label>' +
        '<label class="jn-hint-field"><span>Their mobile (optional)</span>' +
          '<input type="tel" name="jn-hint-phone" placeholder="(555) 123-4567" autocomplete="off"></label>' +
        '<label class="jn-hint-field"><span>Add a note (optional)</span>' +
          '<textarea name="jn-hint-message" rows="3" maxlength="1000"></textarea></label>' +
        '<p class="jn-hint-error" role="alert" hidden></p>' +
        '<p class="jn-hint-note">You can send up to 3 hints per day. Tip: save several pieces to your wishlist to hint a whole set in one go.</p>' +
        '<div class="jn-hint-dialog__actions">' +
          '<button type="button" class="jn-hint-cancel link-faded">Cancel</button>' +
          '<button type="submit" class="jn-hint-send button">Send hint</button>' +
        '</div>' +
      '</form>';
    // Mount inside the triggering modal (if any) so its focus-trap sees clicks in
    // this dialog as "inside" and doesn't close itself; showModal still puts us in
    // the top layer. Falls back to <body> on the PDP / wishlist page.
    (opts.mountIn || document.body).appendChild(dlg);

    var form = dlg.querySelector('form');
    var errEl = dlg.querySelector('.jn-hint-error');
    var sendBtn = dlg.querySelector('.jn-hint-send');

    dlg.addEventListener('click', function (e) {
      if (e.target === dlg || e.target.closest('.jn-hint-cancel') || e.target.closest('[data-jn-hint-close]')) dlg.close();
    });
    dlg.addEventListener('close', function () { dlg.remove(); });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      errEl.hidden = true;
      var email = form.elements['jn-hint-email'].value.trim();
      var phone = form.elements['jn-hint-phone'].value.trim();
      var missing = false;
      var payloadItems = [];
      dlg.querySelectorAll('.jn-hint-item').forEach(function (el) {
        var gid = el.getAttribute('data-gid');
        var hasSize = el.getAttribute('data-hassize') === '1';
        var fixed = el.getAttribute('data-fixed-size');
        var size = '';
        if (fixed) {
          size = fixed;                 // size already chosen — no picker shown
        } else if (hasSize) {
          var ch = el.querySelector('input[type=radio]:checked');
          if (!ch) missing = true; else size = ch.value;
        }
        payloadItems.push({ id: gid, size: size });
      });
      if (missing) { showErr(errEl, 'Please choose a size for each piece.'); return; }
      if (!isEmail(email)) { showErr(errEl, 'Please enter a valid email address.'); return; }
      if (phone && phone.replace(/\D/g, '').length < 7) { showErr(errEl, 'Please enter a valid mobile number.'); return; }

      sendBtn.disabled = true; sendBtn.textContent = 'Sending…';
      sendHint({ recipient: email, phone: phone, message: form.elements['jn-hint-message'].value, items: payloadItems })
        .then(function () { dlg.close(); toast('Hint sent ✓'); if (opts.onSent) opts.onSent(); })
        .catch(function (err) {
          sendBtn.disabled = false; sendBtn.textContent = 'Send hint';
          showErr(errEl, err && err.error === 'rate_limited'
            ? 'That recipient has reached today’s hint limit — please try again tomorrow.'
            : 'Sorry, the hint couldn’t be sent. Please try again.');
        });
    });

    dlg.showModal();
  }

  // Currently-selected variant id on the PDP (the theme keeps it on input[name="id"]).
  function pdpSelectedVariantId(btn) {
    var form = btn.closest('form');
    var inp = form && form.querySelector('input[name="id"]');
    if (inp && /^\d+$/.test(inp.value)) return inp.value;
    try { var v = new URLSearchParams(location.search).get('variant'); if (v && /^\d+$/.test(v)) return v; } catch (e) {}
    return '';
  }

  // The open modal (e.g. quick-buy) this control sits in, if any — so the hint
  // dialog can mount inside it and not trip the modal's outside-click close.
  function overlayFor(el) {
    return (el && el.closest) ? el.closest('quick-buy-modal, [aria-modal="true"], dialog[open]') : null;
  }

  // PDP / quick-buy "Drop a hint" buttons (single product).
  document.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('[data-jn-pdp-hint]') : null;
    if (!b) return;
    e.preventDefault();
    open([{
      gid: b.getAttribute('data-wl-gid'),
      handle: b.getAttribute('data-wl-handle'),
      title: b.getAttribute('data-wl-title'),
      variantId: pdpSelectedVariantId(b)
    }], { mountIn: overlayFor(b) });
  });

  // ---- "Add all to cart" variant resolution ------------------------------
  function av(v) { return v && v.available; }

  function variantForSize(product, size) {
    var idx = sizeOptionIndex(product);
    var key = (idx >= 0) ? ('option' + (idx + 1)) : null;
    if (!key) { var v0 = product.variants.filter(av)[0] || product.variants[0]; return v0 ? v0.id : null; }
    var match = product.variants.filter(function (v) { return String(v[key]) === String(size); });
    var chosen = match.filter(av)[0] || match[0];
    return chosen ? chosen.id : null;
  }

  // Resolve a product.js to a cart variant when the size is unambiguous:
  // one-size, a saved-size (cookie) match, or a single in-stock size. Otherwise
  // flag it as needing a size choice. Fully sold-out products resolve to null.
  function autoResolve(product, preferVariantId) {
    if (!product || !product.variants || !product.variants.length) return { variantId: null };
    if (!product.variants.some(av)) return { variantId: null };
    // A saved variant (from the wishlist) wins when it's still in stock.
    if (preferVariantId) {
      var pv = product.variants.filter(function (v) { return String(v.id) === String(preferVariantId); })[0];
      if (pv && pv.available) return { variantId: pv.id };
    }
    var sizes = sizesFor(product);
    if (!sizes.length) return { variantId: product.variants.filter(av)[0].id };
    var pref = preferredSizeFromCookie(sizes);
    if (pref) { var vid = variantForSize(product, pref); if (vid) return { variantId: vid }; }
    var availSizes = sizes.filter(function (s) { return s.available; });
    if (availSizes.length === 1) return { variantId: variantForSize(product, availSizes[0].value) };
    return { needs: true, sizes: sizes };
  }

  // For "Add all to cart": auto-resolve what we can, and if any items still need a
  // size, ask for just those (chips, saved size pre-selected). Resolves to
  // { items:[{gid,variantId}], cancelled }. On cancel nothing is added.
  function resolveCartVariants(items) {
    var ready = [], ambiguous = [];
    items.forEach(function (it) {
      var r = autoResolve(it.product, it.variantId);
      if (r.needs) ambiguous.push({ gid: it.gid, title: it.title, sizes: r.sizes, product: it.product });
      else if (r.variantId) ready.push({ gid: it.gid, variantId: r.variantId });
    });
    if (!ambiguous.length) return Promise.resolve({ items: ready, cancelled: false });
    return new Promise(function (resolve) {
      showSizePicker(ambiguous, function (picked) {
        if (!picked) { resolve({ items: ready, cancelled: true }); return; }
        resolve({ items: ready.concat(picked), cancelled: false });
      });
    });
  }

  function showSizePicker(entries, done) {
    if (typeof HTMLDialogElement === 'undefined') {
      // Old browsers: best-effort — add the first in-stock size of each.
      done(entries.map(function (e) {
        var s = e.sizes.filter(function (x) { return x.available; })[0] || e.sizes[0];
        return s ? { gid: e.gid, variantId: variantForSize(e.product, s.value) } : null;
      }).filter(Boolean));
      return;
    }

    var itemsHtml = entries.map(function (e, idx) {
      var preSize = preferredSizeFromCookie(e.sizes);
      var chips = e.sizes.map(function (s) {
        var checked = (preSize && String(s.value) === String(preSize)) ? ' checked' : '';
        return '<label class="jn-hint-size' + (s.available ? '' : ' is-soldout') + '">' +
          '<input type="radio" name="jn-sp-size-' + idx + '" value="' + escapeHtml(s.value) + '"' + checked + '>' +
          '<span>' + escapeHtml(s.value) + (s.available ? '' : ' (sold out)') + '</span></label>';
      }).join('');
      return '<div class="jn-hint-item"><div class="jn-hint-item__title">' + escapeHtml(e.title) + '</div>' +
        '<div class="jn-hint-sizes">' + chips + '</div></div>';
    }).join('');

    var dlg = document.createElement('dialog');
    dlg.className = 'jn-hint-dialog';
    dlg.innerHTML =
      '<form class="jn-hint-dialog__inner" novalidate>' +
        '<button type="button" class="jn-hint-close" aria-label="Close" data-jn-sp-close>&times;</button>' +
        '<h2 class="jn-hint-dialog__title">Pick sizes to add</h2>' +
        '<p class="jn-hint-dialog__sub">Choose a size for these pieces to add them to your cart.</p>' +
        '<div class="jn-hint-items">' + itemsHtml + '</div>' +
        '<p class="jn-hint-error" role="alert" hidden></p>' +
        '<div class="jn-hint-dialog__actions">' +
          '<button type="button" class="jn-hint-cancel link-faded" data-jn-sp-cancel>Cancel</button>' +
          '<button type="submit" class="jn-hint-send button">Add to cart</button>' +
        '</div>' +
      '</form>';
    document.body.appendChild(dlg);

    var form = dlg.querySelector('form');
    var errEl = dlg.querySelector('.jn-hint-error');
    var settled = false;
    function finish(val) { if (settled) return; settled = true; done(val); dlg.close(); }

    dlg.addEventListener('click', function (e) {
      if (e.target === dlg || e.target.closest('[data-jn-sp-cancel]') || e.target.closest('[data-jn-sp-close]')) finish(null);
    });
    dlg.addEventListener('cancel', function () { finish(null); });   // Esc
    dlg.addEventListener('close', function () { if (!settled) { settled = true; done(null); } dlg.remove(); });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      errEl.hidden = true;
      var out = [], missing = false;
      entries.forEach(function (ent, idx) {
        var ch = dlg.querySelector('input[name="jn-sp-size-' + idx + '"]:checked');
        if (!ch) { missing = true; return; }
        var vid = variantForSize(ent.product, ch.value);
        if (vid) out.push({ gid: ent.gid, variantId: vid });
      });
      if (missing) { errEl.textContent = 'Please choose a size for each piece.'; errEl.hidden = false; return; }
      finish(out);
    });

    dlg.showModal();
  }

  window.JNHint = { open: open, resolveCartVariants: resolveCartVariants };
})();
